import { useRef, useState, ChangeEvent, FunctionComponent } from 'react';

// Libs
import * as Yup from 'yup';
import { useFormik } from 'formik';
import classnames from 'classnames';
import { uploadHttp } from '@Libs/http';
import uploadApi from '@Libs/api/upload';
import { phoneRegExp, MESSAGE_SUCCESS, MESSAGE_ERROR, useSupportAirTable, CallbackDataType } from '@Libs/utils';

// Contexts
import { useAlertContext } from '@Components/Alert/Context';
import { useViewDetected } from '@Components/Utils/ViewDetected';

// Components
import Upload from '@Components/Upload';
import Image from '@Components/Utils/Image';
import Input from '@Components/UI/Form/Input';
import ButtonLoading from '@Components/UI/ButtonLoading';
import ReactSelect from '@Components/UI/Form/ReactSelect';

// Styles
import styles from './ContactForm.module.scss';

interface ContactFormProps {
    className?: string;
}

type Attachment = { file?: any; id?: number; image?: string };

type TemporaryImage = {
    fileId: string;
    fileUrl: string;
    uploadResult: {
        id: string;
        imageMediaMetadata: {
            width: number;
            height: number;
            rotation: number;
        };
    };
};

type FieldItem = {
    key: string;
    name: string;
};

type selectOption = {
    label: string;
    value: string;
};

const validationSchema = Yup.object({
    phoneNumber: Yup.string()
        .required('Vui lòng điền đầy đủ thông tin!')
        .min(9, 'Vui lòng nhập số điện thoại hợp lệ!')
        .max(10, 'Vui lòng nhập số điện thoại hợp lệ!')
        .matches(phoneRegExp, 'Vui lòng nhập số điện thoại hợp lệ!'),
    email: Yup.string().email('Vui lòng nhập email hợp lệ!'),
    name: Yup.string().required('Vui lòng điền đầy đủ thông tin!'),
    problem: Yup.string().required('Vui lòng điền đầy đủ thông tin!'),
});

const CONTACT_OPTIONS = [
    {
        value: 'Thắc mắc về dịch vụ',
        label: 'Thắc mắc về dịch vụ',
    },
    {
        value: 'Giải đáp tính năng',
        label: 'Giải đáp tính năng',
    },
    {
        value: 'Góp ý cải thiện',
        label: 'Góp ý cải thiện',
    },
    {
        value: 'Báo lỗi',
        label: 'Báo lỗi',
    },
    {
        value: 'Liên hệ hợp tác',
        label: 'Liên hệ hợp tác',
    },
    {
        value: 'Các vấn đề khác',
        label: 'Các vấn đề khác',
    },
];

const initValues = {
    name: '',
    email: '',
    detail: '',
    problem: '',
    phoneNumber: '',
    attachments: [],
};

const AIRTABLE_FIELDS: Array<FieldItem> = [
    { name: 'Name', key: 'name' },
    { name: 'Email', key: 'email' },
    { name: 'Detail', key: 'detail' },
    { name: 'Vấn đề', key: 'problem' },
    { name: 'Phone', key: 'phoneNumber' },
];

const BASE_ID = 'apprV0ryUoLMqsih1';

const BASE_FORM = 'Form liên hệ';

const defaultOption = {
    label: '',
    value: '',
};

const ContactForm: FunctionComponent<ContactFormProps> = ({ className }) => {
    const uploadRef = useRef(null);

    const { deviceType } = useViewDetected();
    const { alert } = useAlertContext();

    const { sendAirTable } = useSupportAirTable(BASE_ID);

    const [attachments, setAttachments] = useState<Attachment>({});
    const [option, setOption] = useState<selectOption>(defaultOption);
    const [loading, setLoading] = useState<boolean>(false);

    const isMobile = deviceType !== 'tablet';

    const callback = (data: CallbackDataType) => {
        setLoading(false);
        setAttachments({});

        if (data.type === 'success') {
            setOption(defaultOption);
            resetForm({ values: initValues });
            alert({
                type: 'success',
                text: MESSAGE_SUCCESS,
                title: 'Gửi thành công',
            });
        } else {
            alert({
                type: 'error',
                text: MESSAGE_ERROR,
                title: 'Đã xảy ra lỗi',
            });
        }
    };

    const handleUploadImages = (uploadedAttachments) => {
        if (uploadedAttachments.length === 0) return [];

        return Promise.all(
            uploadedAttachments.map(async (value) => {
                const result: TemporaryImage = await uploadApi(uploadHttp).uploadTemporaryImage(value.id, value.file);

                return { url: result?.fileUrl };
            }),
        );
    };

    const registerForm = async (values) => {
        setLoading(true);
        const attachmentSubmit = await handleUploadImages(values.attachments);

        const fields = AIRTABLE_FIELDS.reduce((fieldObj, fieldItem: FieldItem) => {
            fieldObj[fieldItem.name] = values[fieldItem.key];
            return fieldObj;
        }, {});

        fields['Attachments'] = attachmentSubmit;

        sendAirTable(BASE_FORM, fields, null, callback);
    };

    const changeValue = (event: ChangeEvent<HTMLInputElement>) => {
        const { value, name } = event.target;

        setFieldValue(name, value.trim(), !!errors[name]);
    };

    const chooseOption = (option: selectOption) => {
        setOption(option);
        setFieldValue('problem', option.value, !!errors['problem']);
    };

    const ChooseAttachment = async (event: ChangeEvent<HTMLInputElement>) => {
        const files: any = Array.from(event.target.files);

        const attachments_upload: Array<Attachment> = await Promise.all(
            files.map(async (file: any, index) => {
                const result = await uploadApi(uploadHttp).generateTemporaryId();
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.addEventListener('load', (ev) => {
                        const fileResolve = {
                            file,
                            type: file.type,
                            size: file.size,
                            filename: file.name,
                            id: result?.ids?.[0] || Object.keys(attachments).length + index,
                        };

                        if (file.type !== 'application/pdf') {
                            fileResolve['image'] = ev.target.result;
                        }

                        resolve(fileResolve);
                    });
                    reader.addEventListener('error', () => {
                        reject(new Error('Upload images fail!'));
                    });
                    reader.readAsDataURL(file);
                });
            }),
        );

        const newObjAttachments = attachments;

        attachments_upload.forEach((attachment) => {
            newObjAttachments[attachment.id] = attachment;
        });

        setAttachments(newObjAttachments);
        setFieldValue('attachments', [...values['attachments'], ...attachments_upload]);

        uploadRef.current.reset();
    };

    const { errors, values, setFieldValue, handleSubmit, isValid, resetForm } = useFormik({
        validationSchema,
        onSubmit: registerForm,
        initialValues: initValues,
    });

    const removeAttachment = (id: string) => {
        const currentAttachments = { ...attachments };
        delete currentAttachments[id];

        const newAttachments = Object.values(currentAttachments).map((attachment) => attachment.file);

        setAttachments(currentAttachments);
        setFieldValue('attachments', newAttachments);
    };

    const renderAttachment = () => {
        return Object.values(attachments).map((attachment) => {
            const isPDF = attachment.type === 'application/pdf';
            const img = isPDF ? '/assets/global/folder.png' : attachment.image;

            return (
                <div className={styles.attachment_wrapper} key={attachment.id}>
                    <Image src={img} className={styles.attachment} width={55} height={55} />
                    <Image
                        width={16}
                        height={16}
                        src={'/assets/global/remove_border.png'}
                        onClick={() => removeAttachment(attachment.id)}
                        className={classnames([styles.image_remove, { [styles.folder]: isPDF }])}
                    />

                    {isPDF && <div className={styles.folder_name}>{attachment.filename}</div>}
                </div>
            );
        });
    };

    const wrapperClassName = classnames([styles.wrapper, { [styles.mobile]: isMobile }, className]);

    return (
        <form onSubmit={handleSubmit} className={wrapperClassName}>
            <Input
                name="name"
                requiredMark
                label={'Họ và tên'}
                value={values.name}
                error={!!errors.name}
                onChange={changeValue}
                errorMessage={errors.name}
                inputClassName={styles.input}
                labelClassName={styles.label}
                className={styles.input_field}
                errorClassName={styles.error_message}
                inputErrorClassName={styles.input_error}
                labelFloatClassName={styles.label_focus}
            />

            <Input
                requiredMark
                name="phoneNumber"
                onChange={changeValue}
                value={values.phoneNumber}
                label="Nhập số điện thoại"
                error={!!errors.phoneNumber}
                inputClassName={styles.input}
                labelClassName={styles.label}
                className={styles.input_field}
                errorMessage={errors.phoneNumber}
                errorClassName={styles.error_message}
                inputErrorClassName={styles.input_error}
                labelFloatClassName={styles.label_focus}
            />

            <Input
                name="email"
                label="Nhập email"
                value={values.email}
                onChange={changeValue}
                errorMessage={errors.email}
                inputClassName={styles.input}
                labelClassName={styles.label}
                className={styles.input_field}
                errorClassName={styles.error_message}
                labelFloatClassName={styles.label_focus}
            />

            <ReactSelect
                requiredMark
                value={option}
                isSearchable={false}
                onChange={chooseOption}
                error={!!errors.problem}
                options={CONTACT_OPTIONS}
                errorMessage={errors.problem}
                labelClassName={styles.label}
                className={styles.select_field}
                selectClassName={styles.select}
                placeholder={'Vấn đề cần liên hệ'}
                errorClassName={styles.error_message}
                labelFloatClassName={styles.label_float}
                selectErrorClassName={styles.select_error}
            />

            <Input
                name="detail"
                value={values.detail}
                onChange={changeValue}
                inputClassName={styles.input}
                labelClassName={styles.label}
                className={styles.input_field}
                labelFloatClassName={styles.label_focus}
                label="Nhập chi tiết vấn đề cần liên hệ"
            />

            <Upload
                multiple
                ref={uploadRef}
                onChange={ChooseAttachment}
                accept="image/*, application/pdf"
                className={styles.upload_wrapper}
            >
                <div className={styles.upload}>Đính kèm tài liệu</div>
            </Upload>

            {Object.keys(attachments).length > 0 && <div className={styles.attachments}>{renderAttachment()}</div>}

            <ButtonLoading
                type="submit"
                loading={loading}
                disabled={!isValid}
                className={classnames([styles.btn_submit, { [styles.loading]: loading }])}
            >
                Gửi ngay
            </ButtonLoading>
        </form>
    );
};

ContactForm.defaultProps = {
    className: '',
};

export default ContactForm;
